import { encodeHTMLEntities } from '@ds/swymkit/utils'

import type { MEDIA_PROCESSING_STATUS } from '~/entities/content/media/composables/media-format'
import type { IMediaType } from '~/entities/content/media/interfaces/media-type'
import { Maturity as SwymQuestionMaturity } from '~/entities/content/question/api/questions'
import type { UserState } from '~/entities/user/user/interfaces/user'

import { dateParser } from '~/features-shared/content/api/parser-contents'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { Event } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { DS6WEvent } from '~/features-shared/content/interfaces/exalead-contents'
import type { IDerivedTypeData } from '~/features-shared/content-derived-type/interfaces/derived-types'
import { EDerivedTypeParent } from '~/features-shared/content-derived-type/interfaces/derived-types'
import { createStyles } from '~/features-shared/content-derived-type/utils/derived-types'
import type { IEntity } from '~/features-shared/libs/interfaces/entity'
import { WedoDS6WMaturity } from '~/features-shared/maturities/interfaces/exalead-maturities'
import { QuestionMaturity, WedoMaturity } from '~/features-shared/maturities/interfaces/maturities'
import type { IFedSearchAttribute, IMember } from '~/features-shared/member/interfaces/search'

import type { Parser } from '~/features-generic/services/interfaces/exalead-parser'

import type { ExaleadResponse } from '~/interfaces/services-dto'

export function toEvent(event: DS6WEvent) {
  const events = {
    [DS6WEvent.Published]: Event.Published,
    [DS6WEvent.Commented]: Event.Commented,
    [DS6WEvent.Answered]: Event.Answered,
    [DS6WEvent.Revised]: Event.Revised,
    [DS6WEvent.Edited]: Event.Edited,
    [DS6WEvent.Ended]: Event.Ended,
    [DS6WEvent.Started]: Event.Started,
    [DS6WEvent.StatusChanged]: Event.StatusChanged,
    [DS6WEvent.Validated]: Event.Validated,
  }
  return events[event] || event
}

export function extractContents(responses: ExaleadResponse): Record<string, string>[] {
  return responses.hits.map(hit => Object.fromEntries(hit.metas.map(el => ([el.name, el.value]))))
}

/**
 * Extract the value by key name from result of FedSearch
 */
export function extractContentByKey(response: Array<{ attributes: IFedSearchAttribute[] }>, keyName: string) {
  return response[0]?.attributes.find((item: any) => item.name === keyName)?.value
}

export const entitiesParser: Parser<IEntity> = (responses) => {
  const extracted = extractContents(responses)
  const entities: IEntity[] = extracted.map(entity => ({
    id: entity.id,
    subject_uri: entity.ds6w_58_resourceUid || `${entity.ds6w_58_containerUid}:${entity.resourceid}`,
    created_at: dateParser(entity.crdate),
    updated_at: dateParser(entity.update),
  }))

  return entities
}

/**
 * TODO
 * /!\ Need to have unit test for the parsing /!\
 */
export const contentsParser: Parser<IContent> = (responses) => {
  const extracted = extractContents(responses)
  const entities = entitiesParser(responses)

  const adapter: Record<string, IContentModel> = {
    'wikitree': IContentModel.Wiki,
    'posts': IContentModel.Post,
    'media': IContentModel.Media,
    'iquestion': IContentModel.Question,
    'iquestion question': IContentModel.Question,
    'survey': IContentModel.Survey,
    'wedo': IContentModel.Wedo,
    'conference': IContentModel.Conference,
    'idea': IContentModel.Idea,
  }

  // eslint-disable-next-line complexity
  const contents = extracted.map((extract, i) => {
    const model = adapter[(extract.swym_source || '').toLowerCase()] as IContentModel || IContentModel.Post
    const content: IContent = {
      ...entities[i],
      description: '',
      community_type: extract.community_type as IContent['community_type'],
      moderated_at: '',
      stats_viewed: 0,
      moderation_status: extract.moderation_status as IContent['moderation_status'],
      title: extract.title || extract.ds6w_58_label,
      // FIXME: Default params
      enhanced_summary: extract.enhanced_summary,
      community_id: extract.community_id,
      community_subject_uri: extract.ds6w_58_containerUid,
      community_title: extract.ds6w_58_community,
      conversation_title: extract.ds6w_58_datasource,
      preview_fingerprint: extract.preview_fingerprint,
      thumbnail_id: extract.preview_media_id,
      preview_media_processing_version: Number.parseInt(extract.preview_media_processing_version, 10) as 0 | 1,
      preview_media_processing_status: extract.preview_media_processing_status as MEDIA_PROCESSING_STATUS,
      preview_original_width: Number.parseInt(extract.preview_original_width, 10),
      preview_original_height: Number.parseInt(extract.preview_original_height, 10),
      preview_url: extract.preview_url || '',
      model,
      published_at: dateParser(extract.pubdate),
      author: {
        uid: '',
        first_name: extract.user_firstname,
        last_name: extract.user_lastname,
        displayName: `${extract.user_firstname} ${extract.user_lastname}`,
        login: extract.username,
        user_state: extract.ut_state as UserState,
      },
      last_contributed_at: dateParser(extract.ds6w_58_lastcontributed_date),
      last_contributed_event: toEvent(extract.ds6w_58_lastcontributed_event as DS6WEvent),
      last_contributed_author: {
        uid: '',
        displayName: `${extract.ds6w_58_lastcontributed_user_firstname} ${extract.ds6w_58_lastcontributed_user_lastname}`,
        last_name: extract.ds6w_58_lastcontributed_user_lastname,
        first_name: extract.ds6w_58_lastcontributed_user_firstname,
        login: extract.ds6w_58_lastcontributed_user_login,

      },
      published: extract.swym_58_publicationState !== 'Draft',
      // maturity: extract.question_type || extract.wedo_status,
    }

    if (model === IContentModel.Media)
      Object.assign(content, _mediaParser(extract))
    else if (model === IContentModel.Idea)
      Object.assign(content, _ideaParser(extract))
    else if (model === IContentModel.Question)
      Object.assign(content, _questionParser(extract))
    else if (model === IContentModel.Wedo)
      Object.assign(content, _wedoParser(extract))
    else if (model === IContentModel.Survey)
      Object.assign(content, _surveyParser(extract))
    else if (model === IContentModel.Conference)
      Object.assign(content, _conferenceParser(extract))

    // Params for setting derived types
    if (extract.ds6w_58_typeId)
      Object.assign(content, _derivedTypeParser(extract))

    return content
  })

  return contents
}

function _surveyParser(content: Record<string, string>) {
  return {
    endDate: content.endDate,
    endHour: content.endHour,
    startDate: content.startDate,
    startHour: content.startHour,
  }
}

function _conferenceParser(content: Record<string, string>) {
  return {
    start_date: content.start_date,
  }
}

const questionMaturitiesMatrix = {
  [SwymQuestionMaturity.Answered]: QuestionMaturity.Answered,
  [SwymQuestionMaturity.Validated]: QuestionMaturity.Validated,
  [SwymQuestionMaturity.NotAnswered]: QuestionMaturity.NotAnswered,
}
function _questionParser(content: Record<string, string>) {
  return {
    maturity: questionMaturitiesMatrix[content.question_type as SwymQuestionMaturity],
  }
}

const wedoMaturitiesMatrix = {
  [WedoDS6WMaturity.Open]: WedoMaturity.Open,
  [WedoDS6WMaturity.InProgress]: WedoMaturity.InProgress,
  [WedoDS6WMaturity.Done]: WedoMaturity.Done,
}
function _wedoParser(content: Record<string, string>) {
  return {
    subject_uri: content.id, // For Wedo, there is not a specific subject uri like "SWYM64:DS54684:WEDO:[ID]"
    maturity: wedoMaturitiesMatrix[content.wedo_status as unknown as WedoDS6WMaturity],
  }
}

function _ideaParser(content: Record<string, string>) {
  return {
    model: IContentModel.Idea,
    status_title: content.status_title,
    status_color: content.ideation_color,
  }
}

function _mediaParser(content: Record<string, string>) {
  return {
    status: content.preview_media_processing_status,
    width: Number.parseInt(content.preview_original_width),
    height: Number.parseInt(content.preview_original_height),
    media_type: content.media_type as IMediaType, // TO REFACTOR
    type: content.media_type as IMediaType,
    file_ext: content.preview_media_file_ext,
  }
}

function _derivedTypeParser(content: Record<string, string>) {
  const obj = {
    model: content.ds6w_58_type === 'swym:Idea' ? IContentModel.DerivedIdea : IContentModel.DerivedPost,
    derived_type: {
      label: encodeHTMLEntities(content.ds6w_58_kind),
      parent: content.ds6w_58_type === 'swym:Idea' ? EDerivedTypeParent.IDEA : EDerivedTypeParent.POST,
      icon: content.ds6w_58_typeFontIcon,
      color: content.ds6w_58_typeColor,
      id: content.ds6w_58_typeId,
    },
  }

  createStyles(obj.derived_type as IDerivedTypeData)

  return obj
}

export const memberParser: Parser<IMember> = (responses) => {
  const extracted = extractContents(responses)
  const entities = extracted.map(entity => ({
    login: entity.login,
  } as IMember))

  return entities
}
